import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Chip,
  Alert,
  Tooltip,
  IconButton,
  Collapse,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Save as SaveIcon,
  Inventory2 as RepackIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  ArrowBack as ArrowBackIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Search as SearchIcon,
  FileDownload as ExportIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import Layout from '../../components/layout/Layout';
import productService from '../../services/productService';
import { getCurrentUser } from '../../services/authService';

// ── Helper: parse any date value (ISO string or Java LocalDateTime array) ────
const parseDate = (dt) => {
  if (!dt) return null;
  if (Array.isArray(dt)) {
    // Java LocalDateTime serialized as [year, month, day, hour, minute, second, nano]
    return new Date(dt[0], dt[1] - 1, dt[2], dt[3] || 0, dt[4] || 0, dt[5] || 0);
  }
  return new Date(dt);
};

// ── Helper: format date only (no time) ──────────────────────────────────────
const fmtDate = (dt) => {
  if (!dt) return '—';
  try {
    const d = parseDate(dt);
    if (!d || isNaN(d.getTime())) return String(dt);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(dt);
  }
};

// ── Excel export helper ──────────────────────────────────────────────────────
const exportToExcel = (historyData) => {
  if (!historyData || historyData.length === 0) return;

  const rows = [];
  historyData.forEach((record) => {
    (record.items || []).forEach((item, idx) => {
      rows.push({
        'Transfer ID': record.id,
        'Date': fmtDate(record.transferredAt),
        'Bulk Product': record.parentProductName || '',
        'SKU': record.parentProductSku || '',
        'Unit': record.parentUnitName || '',
        'Variant': item.repackedProductName || '',
        'Variant UOM': item.repackedUnitName || '',
        'Qty Transferred': parseFloat(item.qtyTransferred || 0),
        'Parent Qty Used': parseFloat(item.parentQtyReduced || 0),
        'Stock After': item.repackedQtyAfter != null ? parseFloat(item.repackedQtyAfter) : '',
        'Notes': idx === 0 ? (record.notes || '') : '',
        'By': idx === 0 ? (record.performedByName || '') : '',
      });
    });
    if (!record.items || record.items.length === 0) {
      rows.push({
        'Transfer ID': record.id,
        'Date': fmtDate(record.transferredAt),
        'Bulk Product': record.parentProductName || '',
        'SKU': record.parentProductSku || '',
        'Unit': record.parentUnitName || '',
        'Variant': '',
        'Variant UOM': '',
        'Qty Transferred': '',
        'Parent Qty Used': '',
        'Stock After': '',
        'Notes': record.notes || '',
        'By': record.performedByName || '',
      });
    }
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Repacking History');
  XLSX.writeFile(wb, `Repacking_History_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

// ── History row (expandable) ─────────────────────────────────────────────────
const HistoryRow = ({ record }) => {
  const [open, setOpen] = useState(false);
  const totalTransferred = (record.items || []).reduce(
    (sum, it) => sum + parseFloat(it.qtyTransferred || 0), 0
  );
  const totalParentReduced = (record.items || []).reduce(
    (sum, it) => sum + parseFloat(it.parentQtyReduced || 0), 0
  );
  const itemUnits = (record.items || [])
    .map((it) => (it.repackedUnitName || '').trim())
    .filter(Boolean);
  const singleDisplayUnit = itemUnits.length > 0 && itemUnits.every((u) => u === itemUnits[0])
    ? itemUnits[0]
    : null;
  const parentQtyUsedDisplay = singleDisplayUnit
    ? `−${totalTransferred.toFixed(2)} ${singleDisplayUnit}`
    : `−${totalParentReduced.toFixed(2)} ${record.parentUnitName || ''}`;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& td': { borderBottom: open ? 'none' : undefined } }}
        onClick={() => setOpen((p) => !p)}
      >
        <TableCell sx={{ py: 1, width: 36 }}>
          <IconButton size="small">
            {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.85rem' }}>
          {record.parentProductName}
          {record.parentProductSku && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({record.parentProductSku})
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ py: 1, fontSize: '0.82rem', color: 'text.secondary' }}>
          {fmtDate(record.transferredAt)}
        </TableCell>
        <TableCell sx={{ py: 1, fontSize: '0.82rem', fontWeight: 700, color: 'warning.main' }} align="right">
          {parentQtyUsedDisplay}
        </TableCell>
        <TableCell sx={{ py: 1, fontSize: '0.82rem' }} align="right">
          +{totalTransferred.toFixed(2)}
        </TableCell>
        <TableCell sx={{ py: 1, fontSize: '0.8rem', color: 'text.secondary', maxWidth: 160 }}>
          {record.notes || '—'}
        </TableCell>
        <TableCell sx={{ py: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
          {record.performedByName || '—'}
        </TableCell>
      </TableRow>

      {/* Expandable detail rows */}
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, px: 0, bgcolor: '#fafbff' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 6, py: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#555', py: 0.5 }}>Variant</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#555', py: 0.5 }} align="right">Qty Transferred</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#555', py: 0.5 }} align="right">Parent Qty Used</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#555', py: 0.5 }} align="right">Stock After</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(record.items || []).map((it) => (
                    <TableRow key={it.id}>
                      <TableCell sx={{ py: 0.5, fontSize: '0.82rem' }}>
                        {it.repackedProductName}
                        {it.repackedUnitName && (
                          <Chip label={it.repackedUnitName} size="small" variant="outlined"
                            sx={{ ml: 1, fontSize: '0.65rem', height: 18 }} />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, fontWeight: 700, color: '#2e7d32', fontSize: '0.82rem' }}>
                        +{parseFloat(it.qtyTransferred || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, fontWeight: 700, color: 'warning.main', fontSize: '0.82rem' }}>
                        −{parseFloat(it.qtyTransferred || 0).toFixed(2)}
                        {it.repackedUnitName ? ` ${it.repackedUnitName}` : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, fontSize: '0.82rem', color: 'text.secondary' }}>
                        {it.repackedQtyAfter != null ? parseFloat(it.repackedQtyAfter).toFixed(2) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Helper: compute effective parent-unit qty consumed per 1 repacked unit ──
// parentConversionRatio is stored in the repacked product's OWN UOM.
// If that UOM is a sub-unit of the parent (e.g. Kg when parent is Bag at 50:1),
// we must convert back to the parent's main unit.
const effectiveParentRatio = (variant, parentProduct) => {
  const ratio = parseFloat(variant?.parentConversionRatio) || 1;
  if (!parentProduct || !variant?.unit?.id) return ratio;

  const repackedUnitId = variant.unit.id;
  const parentUnitId   = parentProduct.unit?.id;

  // Same unit → no conversion
  if (repackedUnitId === parentUnitId) return ratio;

  // Sub Unit 1 match
  if (
    parentProduct.subUnit1?.id === repackedUnitId &&
    parentProduct.subUnit1ConversionRatio &&
    parseFloat(parentProduct.subUnit1ConversionRatio) > 0
  ) {
    return ratio / parseFloat(parentProduct.subUnit1ConversionRatio);
  }

  // Sub Unit 2 match
  if (
    parentProduct.subUnit2?.id === repackedUnitId &&
    parentProduct.subUnit1ConversionRatio &&
    parentProduct.subUnit2ConversionRatio &&
    parseFloat(parentProduct.subUnit1ConversionRatio) > 0 &&
    parseFloat(parentProduct.subUnit2ConversionRatio) > 0
  ) {
    return ratio / (
      parseFloat(parentProduct.subUnit1ConversionRatio) *
      parseFloat(parentProduct.subUnit2ConversionRatio)
    );
  }

  // No known sub-unit relationship → treat ratio as-is
  return ratio;
};

// ── Helper: if repacked UOM is a sub-unit of parent, return the equivalent
// amount in that sub-unit so we can display both (e.g. "0.0200 Bag = 1.0000 Kg")
const subUnitEquivalent = (variant, parentProduct, parentQtyInMainUnit) => {
  if (!parentProduct || !variant?.unit?.id) return null;
  const repackedUnitId = variant.unit.id;
  const repackedUnitLabel = variant.unit?.abbreviation || variant.unit?.name || '';

  if (
    parentProduct.subUnit1?.id === repackedUnitId &&
    parentProduct.subUnit1ConversionRatio &&
    parseFloat(parentProduct.subUnit1ConversionRatio) > 0
  ) {
    return {
      qty: parentQtyInMainUnit * parseFloat(parentProduct.subUnit1ConversionRatio),
      label: repackedUnitLabel,
    };
  }
  if (
    parentProduct.subUnit2?.id === repackedUnitId &&
    parentProduct.subUnit1ConversionRatio &&
    parentProduct.subUnit2ConversionRatio &&
    parseFloat(parentProduct.subUnit1ConversionRatio) > 0 &&
    parseFloat(parentProduct.subUnit2ConversionRatio) > 0
  ) {
    return {
      qty: parentQtyInMainUnit *
        parseFloat(parentProduct.subUnit1ConversionRatio) *
        parseFloat(parentProduct.subUnit2ConversionRatio),
      label: repackedUnitLabel,
    };
  }
  return null;
};

// ── Main component ────────────────────────────────────────────────────────────
const RepackingManagementPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const user = getCurrentUser();
  const companyId = user?.companyId;

  // 'history' | 'new'
  const [view, setView] = useState('history');

  // ── History state ─────────────────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [historyTotalElements, setHistoryTotalElements] = useState(0);
  const HISTORY_PAGE_SIZE = 10;

  // ── Filter state ──────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [activeFilters, setActiveFilters] = useState({ search: '', fromDate: '', toDate: '' });

  const loadHistory = useCallback(async (page = 0, filters = activeFilters) => {
    if (!companyId) return;
    setHistoryLoading(true);
    try {
      const res = await productService.getCompanyRepackingHistory(
        companyId, page, HISTORY_PAGE_SIZE,
        filters.search, filters.fromDate, filters.toDate
      );
      const records = res?.content || res || [];
      setHistory(Array.isArray(records) ? records : []);
      setHistoryTotalPages(res?.totalPages || 1);
      setHistoryTotalElements(res?.totalElements || 0);
      setHistoryPage(page);
    } catch (err) {
      console.error('Error loading repacking history:', err);
      enqueueSnackbar('Failed to load repacking history', { variant: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  }, [companyId, enqueueSnackbar, activeFilters]);

  useEffect(() => {
    if (view === 'history') loadHistory(0, activeFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleApplyFilters = () => {
    const filters = { search: searchText, fromDate, toDate };
    setActiveFilters(filters);
    loadHistory(0, filters);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setFromDate('');
    setToDate('');
    const filters = { search: '', fromDate: '', toDate: '' };
    setActiveFilters(filters);
    loadHistory(0, filters);
  };

  const hasActiveFilters = activeFilters.search || activeFilters.fromDate || activeFilters.toDate;

  // ── Transfer form state (preserved exactly as before) ─────────────────────
  const [products, setProducts] = useState([]);
  const [parentProduct, setParentProduct] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [transferQtys, setTransferQtys] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [transferError, setTransferError] = useState('');
  const qtyRefs = useRef([]);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const res = await productService.getAllProductsForManagement(0, 500, 'name', 'asc');
      const list = res?.content || res || [];
      setProducts(list.filter((p) => !p.parentProductId));
    } catch (err) {
      enqueueSnackbar('Failed to load products', { variant: 'error' });
    } finally {
      setLoadingProducts(false);
    }
  }, [enqueueSnackbar]);

  const loadVariants = useCallback(async () => {
    if (!parentProduct?.id) return;
    setLoadingVariants(true);
    setVariants([]);
    setTransferQtys({});
    setTransferError('');
    try {
      const data = await productService.getRepackedVariants(parentProduct.id);
      setVariants(Array.isArray(data) ? data : []);
    } catch {
      enqueueSnackbar('Failed to load repacked variants', { variant: 'error' });
    } finally {
      setLoadingVariants(false);
    }
  }, [parentProduct?.id, enqueueSnackbar]);

  useEffect(() => {
    if (view === 'new') loadProducts();
  }, [view, loadProducts]);

  useEffect(() => { loadVariants(); }, [loadVariants]);

  const handleQtyChange = (variantId, value) => {
    setTransferError('');
    setTransferQtys((prev) => ({ ...prev, [variantId]: value }));
  };

  const totalParentReduction = variants.reduce((sum, v) => {
    const qty = parseFloat(transferQtys[v.id] || 0);
    if (qty > 0) return sum + qty * effectiveParentRatio(v, parentProduct);
    return sum;
  }, 0);

  const parentAvailableQty = parseFloat(parentProduct?.quantity || 0);
  const parentUnitLabel = parentProduct?.unit?.abbreviation || parentProduct?.unit?.name || '';
  const parentSubUnitRatio = parseFloat(parentProduct?.subUnit1ConversionRatio || 0);
  const parentSubUnitLabel = parentProduct?.subUnit1?.abbreviation || parentProduct?.subUnit1?.name || '';
  const remainingParentQty = parentAvailableQty - totalParentReduction;
  const availableStockDisplayQty = parentSubUnitRatio > 0
    ? (remainingParentQty * parentSubUnitRatio)
    : remainingParentQty;
  const availableStockDisplayLabel = parentSubUnitRatio > 0 ? parentSubUnitLabel : parentUnitLabel;

  const handleTransfer = async () => {
    setTransferError('');
    const items = variants
      .filter((v) => parseFloat(transferQtys[v.id] || 0) > 0)
      .map((v) => ({ repackedProductId: v.id, qty: parseFloat(transferQtys[v.id]) }));

    if (items.length === 0) {
      setTransferError('Enter transfer quantity for at least one repacked variant.');
      return;
    }
    if (totalParentReduction > parentAvailableQty + 0.0001) {
      setTransferError(
        `Insufficient parent stock. Available: ${parentAvailableQty.toFixed(2)}, Required: ${totalParentReduction.toFixed(2)}`
      );
      return;
    }

    setSaving(true);
    try {
      await productService.doRepackingTransfer(parentProduct.id, {
        notes: notes.trim() || null,
        items,
      });
      enqueueSnackbar('Repacking transfer completed successfully!', { variant: 'success' });

      setParentProduct(null);
      setProductSearch('');
      setVariants([]);
      setTransferQtys({});
      setNotes('');

      setView('history');
      loadHistory(0, activeFilters);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Transfer failed';
      setTransferError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelNewTransfer = () => {
    setParentProduct(null);
    setProductSearch('');
    setVariants([]);
    setTransferQtys({});
    setNotes('');
    setTransferError('');
    setView('history');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          maxWidth: '1200px',
          margin: '0 auto',
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
          '& input[type=number]': { MozAppearance: 'textfield' },
        }}
      >
        {/* ── Page Header ───────────────────────────────────────────────── */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RepackIcon sx={{ fontSize: 28, color: '#1a237e' }} />
            <Typography variant="h5" fontWeight="800" sx={{ color: '#1a237e', letterSpacing: '-0.5px' }}>
              Repacking Management
            </Typography>
          </Box>

          {view === 'history' ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Export to Excel">
                <span>
                  <IconButton
                    onClick={() => exportToExcel(history)}
                    disabled={historyLoading || history.length === 0}
                    sx={{ color: '#2e7d32' }}
                  >
                    <ExportIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refresh history">
                <IconButton onClick={() => loadHistory(historyPage, activeFilters)} disabled={historyLoading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setView('new')}
                sx={{ bgcolor: '#311b92', '&:hover': { bgcolor: '#1a237e' }, fontWeight: 700, borderRadius: '6px', boxShadow: 'none' }}
              >
                Add New Transfer
              </Button>
            </Box>
          ) : (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleCancelNewTransfer}
              sx={{ fontWeight: 700, borderRadius: '6px' }}
            >
              Back to History
            </Button>
          )}
        </Box>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* HISTORY VIEW                                                    */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {view === 'history' && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>

            {/* ── History header + filter bar ───────────────────────────── */}
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HistoryIcon sx={{ color: '#311b92', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight="800" color="#311b92">
                  Transfer History
                </Typography>
                {historyTotalElements > 0 && (
                  <Chip
                    label={`${historyTotalElements} record${historyTotalElements !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 20, bgcolor: 'rgba(49,27,146,0.08)', color: '#311b92' }}
                  />
                )}
              </Box>

              {/* Filter row */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-end" flexWrap="wrap">
                <TextField
                  size="small"
                  placeholder="Search by product name, SKU or barcode..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 280, flex: 1 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="From Date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To Date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FilterIcon />}
                    onClick={handleApplyFilters}
                    sx={{ bgcolor: '#311b92', '&:hover': { bgcolor: '#1a237e' }, fontWeight: 700, boxShadow: 'none', whiteSpace: 'nowrap' }}
                  >
                    Apply
                  </Button>
                  {hasActiveFilters && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                      sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>
              </Stack>
            </Box>

            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : history.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                <RepackIcon sx={{ fontSize: 56, color: 'grey.300' }} />
                <Typography variant="body1" color="text.secondary" fontWeight="600">
                  {hasActiveFilters ? 'No records match your filter' : 'No repacking transfers yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {hasActiveFilters
                    ? 'Try adjusting your search or date range.'
                    : <>Click <b>Add New Transfer</b> to repack bulk products into smaller variants.</>}
                </Typography>
                {!hasActiveFilters && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setView('new')}
                    sx={{ mt: 1, bgcolor: '#311b92', '&:hover': { bgcolor: '#1a237e' }, fontWeight: 700, borderRadius: '6px', boxShadow: 'none' }}
                  >
                    Add New Transfer
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f7f7fb' }}>
                        <TableCell sx={{ width: 36 }} />
                        <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#1a237e' }}>Bulk Product</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#1a237e' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#1a237e' }} align="right">Parent Qty Used</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#1a237e' }} align="right">Total Transferred</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#1a237e' }}>Notes</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#1a237e' }}>By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((record) => (
                        <HistoryRow key={record.id} record={record} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination — always visible */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Showing {historyPage * HISTORY_PAGE_SIZE + 1}–{Math.min((historyPage + 1) * HISTORY_PAGE_SIZE, historyTotalElements)} of {historyTotalElements} records
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={historyPage === 0 || historyLoading}
                      onClick={() => loadHistory(historyPage - 1, activeFilters)}
                      sx={{ minWidth: 80, fontWeight: 700 }}
                    >
                      ← Prev
                    </Button>
                    <Typography variant="caption" sx={{ px: 1, fontWeight: 700, color: '#311b92' }}>
                      {historyPage + 1} / {historyTotalPages}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={historyPage >= historyTotalPages - 1 || historyLoading}
                      onClick={() => loadHistory(historyPage + 1, activeFilters)}
                      sx={{ minWidth: 80, fontWeight: 700 }}
                    >
                      Next →
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* NEW TRANSFER FORM (all original logic preserved)               */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {view === 'new' && (
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>

            <Typography variant="caption" sx={{ fontWeight: 800, color: '#311b92', textTransform: 'uppercase', letterSpacing: '1px', mb: 0.5, display: 'block' }}>
              Select Bulk Product
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(opt) =>
                    typeof opt === 'object' ? `${opt.name || ''} (${opt.sku || ''})` : ''
                  }
                  value={parentProduct}
                  onChange={(_, v) => {
                    setParentProduct(v);
                    setTransferQtys({});
                    setNotes('');
                    setTransferError('');
                  }}
                  onInputChange={(_, v) => setProductSearch(v)}
                  inputValue={productSearch}
                  loading={loadingProducts}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth placeholder="Search product..." variant="outlined" size="small" />
                  )}
                />
              </Box>

              {parentProduct && (
                <Box sx={{ display: 'flex', gap: 2, bgcolor: 'rgba(49,27,146,0.04)', p: 1.5, px: 2, borderRadius: '8px', border: '1px solid rgba(49,27,146,0.1)', flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>Main Unit</Typography>
                    <Typography variant="body2" fontWeight="700" color="#311b92">{parentUnitLabel || '—'}</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>Available Stock</Typography>
                    <Typography variant="body2" fontWeight="700" color={remainingParentQty < 0 ? 'error.main' : '#2e7d32'}>
                      {availableStockDisplayQty.toFixed(2)} {availableStockDisplayLabel}
                    </Typography>
                  </Box>
                  <Tooltip title="Refresh variants">
                    <IconButton size="small" onClick={loadVariants}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {/* Variants table */}
            {parentProduct && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#311b92', textTransform: 'uppercase', letterSpacing: '1px', mb: 1, display: 'block' }}>
                  Repacked Variants — Enter Transfer Quantity
                </Typography>

                {transferError && (
                  <Alert severity="error" onClose={() => setTransferError('')} sx={{ mb: 2 }}>
                    {transferError}
                  </Alert>
                )}

                {loadingVariants ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : variants.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <InfoIcon color="info" />
                    <Typography variant="body2" color="text.secondary">
                      No repacked variants defined for this product. Open the product and use the <b>Repacking</b> tab to add variants first.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ borderRadius: '10px', border: '1px solid rgba(0,0,0,0.07)', bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f7f7fb' }}>
                          <TableCell sx={{ color: '#666', fontWeight: 700, fontSize: '0.7rem', width: 36 }}>#</TableCell>
                          <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }}>Variant Name</TableCell>
                          <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }}>UOM</TableCell>
                          <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }} align="right">Conv. Ratio</TableCell>
                          <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }} align="right">Current Stock</TableCell>
                          <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }} align="right">Parent Qty / Unit</TableCell>
                          <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem', minWidth: 130 }} align="right">Transfer Qty</TableCell>
                          <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }} align="right">Stock After</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {variants.map((v, idx) => {
                          const transferQty = parseFloat(transferQtys[v.id] || 0);
                          const effRatio = effectiveParentRatio(v, parentProduct);
                          const rawRatio = parseFloat(v.parentConversionRatio || 0) || 1;
                          const variantUnitLabel = v.unit?.abbreviation || v.unit?.name || '';
                          const currentStock = parseFloat(v.quantity || 0);
                          const stockAfter = currentStock + transferQty;
                          const parentUsed = transferQty > 0 ? transferQty * effRatio : 0;
                          const parentUsedDisplay = transferQty > 0 ? transferQty * rawRatio : 0;

                          return (
                            <TableRow key={v.id} sx={{ '&:hover': { bgcolor: '#f9f9ff' } }}>
                              <TableCell sx={{ color: '#ccc', fontWeight: 700, py: 1 }}>{idx + 1}</TableCell>
                              <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.85rem' }}>{v.name}</TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <Chip label={v.unit?.abbreviation || v.unit?.name || '—'} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1, fontSize: '0.8rem', color: '#666' }}>
                                {rawRatio.toFixed(2)} {variantUnitLabel}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1, fontWeight: 700, fontSize: '0.85rem', color: currentStock === 0 ? 'error.main' : 'text.primary' }}>
                                {currentStock.toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1, fontSize: '0.8rem', color: parentUsed > 0 ? 'warning.main' : '#aaa' }}>
                                {parentUsed > 0 ? `${parentUsedDisplay.toFixed(2)} ${variantUnitLabel}` : '—'}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                <TextField
                                  variant="standard"
                                  type="number"
                                  placeholder="0"
                                  value={transferQtys[v.id] ?? ''}
                                  onChange={(e) => handleQtyChange(v.id, e.target.value)}
                                  onWheel={(e) => e.target.blur()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      qtyRefs.current[idx + 1]?.focus();
                                    }
                                  }}
                                  inputRef={(el) => (qtyRefs.current[idx] = el)}
                                  InputProps={{
                                    disableUnderline: true,
                                    sx: { fontWeight: 700, fontSize: '0.9rem', color: '#c62828' },
                                    inputProps: { style: { textAlign: 'right' }, min: 0 },
                                  }}
                                  sx={{ width: 110 }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1, fontWeight: 700, fontSize: '0.85rem', color: transferQty > 0 ? '#2e7d32' : '#aaa' }}>
                                {transferQty > 0 ? stockAfter.toFixed(2) : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Notes + Action bar */}
                {variants.length > 0 && (
                  <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <TextField
                      size="small"
                      label="Notes (optional)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="e.g. Morning repacking batch"
                      sx={{ minWidth: 280, flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleTransfer}
                      disabled={saving || totalParentReduction === 0}
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        bgcolor: '#311b92', '&:hover': { bgcolor: '#1a237e' },
                        px: 3, py: 1, borderRadius: '4px', fontWeight: 700, fontSize: '0.8rem', boxShadow: 'none', minWidth: 160,
                      }}
                    >
                      {saving ? 'Transferring…' : 'Confirm Transfer'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Layout>
  );
};

export default RepackingManagementPage;
